// utils/googlePlaces.js - Updated to use ERPNext API
class GooglePlacesService {
  constructor() {
    this.baseUrl = process.env.REACT_APP_ERP_API_BASE_URL || process.env.ERP_API_BASE_URL;
    this.apiKey = process.env.REACT_APP_E_ADMIN_API_KEY || process.env.E_ADMIN_API_KEY;
    this.apiSecret = process.env.REACT_APP_E_ADMIN_API_SECRET || process.env.E_ADMIN_API_SECRET;
    this.debug = process.env.REACT_APP_NODE_ENV === 'development';
    
    if (this.debug) {
      // console.log('process.env.NODE_ENV inside:', process.env.REACT_APP_NODE_ENV);
      // console.log('GooglePlacesService initialized with ERPNext:', this.baseUrl);
      // console.log('API Key present:', !!this.apiKey);
      // console.log('API Secret present:', !!this.apiSecret);
    }
  }

  // Get authorization headers for ERPNext
  getAuthHeaders() {
    return {
      'Authorization': `token ${this.apiKey}:${this.apiSecret}`,
      'Content-Type': 'application/json',
    };
  }

  // Search for business places
  async searchBusinesses(query, location = null) {
    if (this.debug) {
      // console.log('Starting business search:', { query, location });
    }
    
    if (!query || query.length < 2) {
      if (this.debug) {
        console.log('Query too short, skipping search');
      }
      return [];
    }
    
    try {
      const response = await fetch(`${this.baseUrl}onboard_api.api.business_profile.search_businesses`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ query, location })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (this.debug) {
        // console.log('Search API Response:', result);
      }
      
      if (result.message && result.message.success) {
        const filteredResults = result.message.data.map(place => ({
          place_id: place.place_id,
          name: place.name,
          formatted_address: place.formatted_address,
          business_status: place.business_status,
          types: place.types || [],
          rating: place.rating,
          user_ratings_total: place.user_ratings_total,
          geometry: place.geometry
        }));
        
        if (this.debug) {
          // console.log('Filtered results:', filteredResults);
        }
        
        return filteredResults;
      } else {
        throw new Error(result.message?.error || 'Search failed');
      }
    } catch (error) {
      console.error('Error searching businesses:', error);
      return [];
    }
  }

  // Get detailed business information
  async getBusinessDetails(placeId) {
    if (this.debug) {
      // console.log('Fetching business details for place ID:', placeId);
    }
    
    try {
      const response = await fetch(`${this.baseUrl}onboard_api.api.business_profile.get_business_details`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ placeId })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (this.debug) {
        // console.log('Business details API response:', result);
      }
      
      if (result.message && result.message.success) {
        const businessData = result.message.data;
        
        if (this.debug) {
          // console.log('Formatted business data:', businessData);
        }
        
        return businessData;
      } else {
        throw new Error(result.message?.error || 'Failed to fetch business details');
      }
    } catch (error) {
      console.error('Error fetching business details:', error);
      throw error;
    }
  }

  async getUserLocation() {
  // Never request user location, always use UK default
  const ukDefaultLocation = { lat: 54.7023545, lng: -3.2765753 };
  
  if (this.debug) {
    // console.log('Using hardcoded UK location (no user permission required):', ukDefaultLocation);
  }
  
  return Promise.resolve(ukDefaultLocation);
}
  // Get user's location for location-based search
  async getUserLocation_1() {
    return new Promise((resolve, reject) => {
      if (this.debug) {
        console.log('Requesting user location...');
      }
      
      if (!navigator.geolocation) {
        if (this.debug) {
          console.log('Geolocation not supported');
        }
        reject(new Error('Geolocation is not supported'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          if (this.debug) {
            console.log('Got user location:', location);
          }
          resolve(location);
        },
        (error) => {
          if (this.debug) {
            console.warn('Location access denied, using default location:', error);
          }
          // Default to UK center if location is denied
          resolve({ lat: 54.7023545, lng: -3.2765753 });
        },
        {
          timeout: 5000,
          enableHighAccuracy: false
        }
      );
    });
  }
}

// Onboarding API Service for ERPNext
class OnboardingService {
  constructor() {
    this.baseUrl = process.env.REACT_APP_ERP_API_BASE_URL || process.env.ERP_API_BASE_URL;
    this.apiKey = process.env.REACT_APP_E_ADMIN_API_KEY || process.env.E_ADMIN_API_KEY;
    this.apiSecret = process.env.REACT_APP_E_ADMIN_API_SECRET || process.env.E_ADMIN_API_SECRET;
  }

  // Get authorization headers for ERPNext
  getAuthHeaders() {
    return {
      'Authorization': `token ${this.apiKey}:${this.apiSecret}`,
      'Content-Type': 'application/json',
    };
  }

    /**
   * Check if a website URL is reachable
   * @param {string} websiteUrl - The website URL to check
   * @returns {Promise<Object>} - Promise resolving to reachability status
   */
  async checkWebsiteReachability(websiteUrl) {
    try {
      // Strip http/https from URL if present for consistency
      const cleanUrl = websiteUrl.replace(/^(https?:\/\/)/, '');

      const response = await fetch(`${this.baseUrl}onboard_api.api.business_profile.check_website_reachability`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ websiteUrl: cleanUrl }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.message?.success) {
        return {
          reachable: result.message.reachable,
          error: result.message.reachable ? null : result.message.message,
          statusCode: result.message.status_code,
          note: result.message.message
        };
      } else {
        throw new Error(result.message?.error || 'Website reachability check failed');
      }
    } catch (error) {
      console.error('Error checking website reachability:', error);
      return {
        reachable: false,
        error: error.message || 'Failed to check website reachability'
      };
    }
}  
/**
 * Save complete business onboarding data
 * @param {Object} onboardingData - Complete onboarding payload
 * @returns {Promise<Object>} - Promise resolving to save result
 */
async saveBusinessOnboarding(onboardingData) {
  try {
    const response = await fetch(`${this.baseUrl}onboard_api.api.business_profile.save_business_onboarding`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(onboardingData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (result.message?.success) {
      return {
        success: true,
        data: result.message.data,
        message: result.message.message
      };
    } else {
      throw new Error(result.message?.error || 'Failed to save onboarding data');
    }
  } catch (error) {
    console.error('Error saving business onboarding:', error);
    return {
      success: false,
      error: error.message || 'Failed to save onboarding data'
    };
  }
}
  async saveOnboardingData(data) {
    try {
      const response = await fetch(`${this.baseUrl}onboard_api.api.business_profile.save_onboarding_data`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.message && result.message.success) {
        return result.message.data;
      } else {
        throw new Error(result.message?.error || 'Failed to save onboarding data');
      }
    } catch (error) {
      console.error('Error saving onboarding data:', error);
      throw error;
    }
  }

  async getOnboardingData(id) {
    try {
      const response = await fetch(`${this.baseUrl}onboard_api.api.business_profile.get_onboarding_data?id=${encodeURIComponent(id)}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.message && result.message.success) {
        return result.message.data;
      } else {
        throw new Error(result.message?.error || 'Failed to fetch onboarding data');
      }
    } catch (error) {
      console.error('Error fetching onboarding data:', error);
      throw error;
    }
  }

/**
 * Combined email verification - checks user existence and sends OTP in one flow
 * @param {string} email - Email address
 * @returns {Promise<Object>} - Promise resolving to verification status
 */
async verifyEmailAndSendOTP(email) {
  try {
    // Step 1: Check if user exists
    const userCheck = await this.checkUserExists(email);
    
    if (!userCheck.success) {
      return {
        success: false,
        error: userCheck.error,
        step: 'user_check'
      };
    }
    
    // If user exists, return early with error
    if (userCheck.userExists) {
      return {
        success: false,
        userExists: true,
        error: `A user account with email address '${email}' already exists. Please use a different email address.`,
        step: 'user_exists'
      };
    }
    
    // Step 2: User doesn't exist, send OTP
    const otpResult = await this.sendEmailOTP(email);
    
    if (otpResult.success) {
      return {
        success: true,
        userExists: false,
        otpSent: true,
        message: otpResult.message || 'OTP sent successfully'
      };
    } else {
      return {
        success: false,
        userExists: false,
        otpSent: false,
        error: otpResult.error,
        step: 'otp_send'
      };
    }
    
  } catch (error) {
    console.error('Error in combined email verification:', error);
    return {
      success: false,
      error: 'Unable to verify email address. Please try again.',
      step: 'network_error'
    };
  }
}
  /**
 * Send email OTP for verification
 * @param {string} email - Email address
 * @returns {Promise<Object>} - Promise resolving to the API response
 */
async sendEmailOTP(email) {
  try {
    const response = await fetch(`${this.baseUrl}onboard_api.api.business_profile.send_email_otp`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (result.message?.success) {
      return {
        success: true,
        message: result.message.message
      };
    } else {
      throw new Error(result.message?.error || 'Failed to send email OTP');
    }
  } catch (error) {
    console.error('Error sending email OTP:', error);
    return {
      success: false,
      error: error.message || 'Failed to send email OTP'
    };
  }
}

/**
 * Verify email OTP
 * @param {string} email - Email address
 * @param {string} otp - OTP code
 * @returns {Promise<Object>} - Promise resolving to the verification result
 */
async verifyEmailOTP(email, otp) {
  try {
    const response = await fetch(`${this.baseUrl}onboard_api.api.business_profile.verify_email_otp`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ email, otp }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (result.message?.success) {
      return {
        success: true,
        message: result.message.message
      };
    } else {
      throw new Error(result.message?.error || 'Failed to verify email OTP');
    }
  } catch (error) {
    console.error('Error verifying email OTP:', error);
    return {
      success: false,
      error: error.message || 'Failed to verify email OTP'
    };
  }
}

/**
 * Send SMS OTP for verification
 * @param {string} phoneNumber - Phone number
 * @returns {Promise<Object>} - Promise resolving to the API response
 */
async sendSMSOTP(phoneNumber) {
  try {
    const response = await fetch(`${this.baseUrl}onboard_api.api.business_profile.send_sms_otp`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ phoneNumber }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (result.message?.success) {
      return {
        success: true,
        message: result.message.message,
        debugOtp: result.message.debug_otp // Remove in production
      };
    } else {
      throw new Error(result.message?.error || 'Failed to send SMS OTP');
    }
  } catch (error) {
    console.error('Error sending SMS OTP:', error);
    return {
      success: false,
      error: error.message || 'Failed to send SMS OTP'
    };
  }
}

/**
 * Verify SMS OTP
 * @param {string} phoneNumber - Phone number
 * @param {string} otp - OTP code
 * @returns {Promise<Object>} - Promise resolving to the verification result
 */
async verifySMSOTP(phoneNumber, otp) {
  try {
    const response = await fetch(`${this.baseUrl}onboard_api.api.business_profile.verify_sms_otp`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ phoneNumber, otp }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (result.message?.success) {
      return {
        success: true,
        message: result.message.message
      };
    } else {
      throw new Error(result.message?.error || 'Failed to verify SMS OTP');
    }
  } catch (error) {
    console.error('Error verifying SMS OTP:', error);
    return {
      success: false,
      error: error.message || 'Failed to verify SMS OTP'
    };
  }
}

  /**
   * Scrape website data for business profile
   * @param {string} websiteUrl - The website URL to scrape
   * @returns {Promise<Object>} - Promise resolving to the scraped data
   */
  async scrapeWebsite(websiteUrl) {
    try {
      // Strip http/https from URL if present
      const cleanUrl = websiteUrl.replace(/^(https?:\/\/)/, '');

      const response = await fetch(`${this.baseUrl}onboard_api.api.business_profile.scrape_website`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ websiteUrl: cleanUrl }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.message?.success) {
        // console.log('scrapeWebsite',this.formatScrapedData(result.message.data));
        return {
          success: true,
          data: this.formatScrapedData(result.message.data)
        };
      } else {
        throw new Error(result.message?.error || 'Website scraping failed');
      }
    } catch (error) {
      console.error('Error scraping website:', error);
      return {
        success: false,
        error: error.message || 'Failed to scrape website data',
        data: null
      };
    }
  }

  /**
   * Format the scraped data for display or further processing
   * @param {Object} data - The raw data returned from scrapeWebsite
   * @returns {Object} - Formatted data
   */
  formatScrapedData(data) {
    if (!data) return null;

    return {
      website: data.website || '',
      businessOverview: data.business_overview || '',
      productsServices: [...new Set(data.products_services || [])], // Remove duplicates
      industry: data.industry || '',
      business_name: data.business_name || '',
      socialMedia: {
        facebook: data.social_media?.facebook || '',
        twitter: data.social_media?.twitter || '',
        linkedin: data.social_media?.linkedin || '',
        instagram: data.social_media?.instagram || '',
      }
    };
  }

/**
 * Check if user already exists with the given email
 * @param {string} email - Email address to check
 * @returns {Promise<Object>} - Promise resolving to user existence status
 */
  async checkUserExists(email) {
    try {
      const response = await fetch(`${this.baseUrl}onboard_api.api.business_profile.check_user_exists`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.message?.success) {
        return {
          success: true,
          userExists: result.message.user_exists,
          message: result.message.message,
          userDetails: result.message.user_details
        };
      } else {
        throw new Error(result.message?.error || 'Failed to check user existence');
      }
    } catch (error) {
      console.error('Error checking user existence:', error);
      return {
        success: false,
        error: error.message || 'Failed to verify email address'
      };
    }
  }
}



// Create and export instances
const googlePlacesService = new GooglePlacesService();
const onboardingService = new OnboardingService();

export default googlePlacesService;
export { onboardingService };